import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { products as productsApi } from '../services/api.js';
import { useAuth } from '../auth/AuthContext.jsx';
import './ProductDetail.css';

/* ─── Static data ──────────────────────────────────────────────────────────── */

const CONTROL_TAGS = [
  { id: 'CTRL01', name: 'MFA_FULL_ORG_WIDE', impact: -2, desc: 'MFA required across all critical systems' },
  { id: 'CTRL02', name: 'MFA_PARTIAL', impact: +1, desc: 'MFA on some systems, not consistent' },
  { id: 'CTRL03', name: 'NO_MFA_PRESENT', impact: +3, desc: 'No MFA on key systems' },
  { id: 'CTRL04', name: 'EDR_DEPLOYED_AND_MONITORED', impact: -1, desc: 'EDR installed and actively monitored' },
  { id: 'CTRL05', name: 'NO_ENDPOINT_PROTECTION', impact: +2, desc: 'Lacks modern endpoint protection' },
  { id: 'CTRL06', name: 'IMMUTABLE_BACKUPS', impact: -2, desc: 'Backups protected from ransomware' },
  { id: 'CTRL07', name: 'BACKUPS_UNTESTED', impact: +1, desc: 'Backups exist but untested' },
  { id: 'CTRL08', name: 'INCIDENT_RESPONSE_PLAN_TESTED', impact: -1, desc: 'Tested IR plan' },
  { id: 'CTRL09', name: 'NO_FORMAL_IR_PLAN', impact: +1, desc: 'No structured IR plan' },
  { id: 'CTRL10', name: 'VULNERABILITY_SCANNING_QUARTERLY', impact: -1, desc: 'Regular vuln scans' },
  { id: 'CTRL11', name: 'NO_PATCHING_POLICY', impact: +2, desc: 'No patch management' },
  { id: 'CTRL12', name: 'PRIVILEGED_ACCESS_MANAGEMENT', impact: -1, desc: 'PAM in place' },
  { id: 'CTRL13', name: 'NO_PRIVILEGED_ACCESS_CONTROLS', impact: +2, desc: 'No admin access controls' },
  { id: 'CTRL14', name: 'EMAIL_SECURITY_ADVANCED', impact: -1, desc: 'Advanced email filtering' },
  { id: 'CTRL15', name: 'NO_SECURITY_AWARENESS_TRAINING', impact: +1, desc: 'No security training' },
  { id: 'CTRL16', name: 'SECURITY_TEAM_DEDICATED', impact: -1, desc: 'Dedicated security team' },
  { id: 'CTRL17', name: 'NO_SECURITY_OWNER', impact: +2, desc: 'No one responsible for security' },
  { id: 'CTRL18', name: 'SOC2_TYPE2_CURRENT', impact: -1, desc: 'Current SOC 2 Type II' },
  { id: 'CTRL19', name: 'THIRD_PARTY_RISK_PROGRAM', impact: -1, desc: 'Vendor risk program' },
  { id: 'CTRL20', name: 'PRIOR_CARRIER_NONRENEWAL_FOR_CYBER', impact: +2, desc: 'Prior carrier nonrenewal' },
];

const EXPOSURE_TAGS = [
  { id: 'EXP01', name: 'HIGH_PII_VOLUME', impact: +1, desc: 'Stores large amounts of PII' },
  { id: 'EXP02', name: 'PHI_DATA', impact: +2, desc: 'Handles protected health information' },
  { id: 'EXP03', name: 'PCI_PAYMENT_DATA', impact: +1, desc: 'Processes payment card data' },
  { id: 'EXP04', name: 'FUNDS_TRANSFER_FUNCTION', impact: +2, desc: 'Initiates wire transfers' },
  { id: 'EXP05', name: 'ADMIN_ACCESS_CLIENT_NETWORKS', impact: +2, desc: 'Admin access to customer systems' },
  { id: 'EXP06', name: 'MULTI_TENANT_SAAS', impact: +1, desc: 'Multi-tenant shared infrastructure' },
  { id: 'EXP07', name: 'PUBLIC_API_EXPOSED', impact: +1, desc: 'Public APIs' },
  { id: 'EXP08', name: 'UPTIME_GUARANTEE_CONTRACT', impact: +1, desc: 'Contractual uptime guarantees' },
  { id: 'EXP09', name: 'CRITICAL_INFRA_DEPENDENCY', impact: +3, desc: 'Supports critical infrastructure' },
  { id: 'EXP10', name: 'THIRD_PARTY_DATA_HOSTING', impact: +1, desc: 'Hosts third-party data' },
  { id: 'EXP11', name: 'CLOUD_CONCENTRATION_SINGLE_PROVIDER', impact: +1, desc: 'Single cloud provider dependency' },
  { id: 'EXP12', name: 'OT_SYSTEMS_CONNECTED', impact: +2, desc: 'OT/industrial control systems' },
  { id: 'EXP13', name: 'INTERNATIONAL_OPERATIONS', impact: +1, desc: 'Multi-country operations' },
  { id: 'EXP14', name: 'REGULATED_INDUSTRY_CLIENTS', impact: +1, desc: 'Serves regulated industries' },
  { id: 'EXP15', name: 'LARGE_ENTERPRISE_CLIENT_BASE', impact: +1, desc: 'Large enterprise customers' },
  { id: 'EXP16', name: 'EMBEDDED_IN_FINANCIAL_WORKFLOW', impact: +2, desc: 'Embedded in financial systems' },
  { id: 'EXP17', name: 'PRIVILEGED_USER_DENSITY_HIGH', impact: +1, desc: 'High admin user ratio' },
  { id: 'EXP18', name: 'LEGACY_SYSTEM_DEPENDENCY', impact: +1, desc: 'Relies on legacy systems' },
  { id: 'EXP19', name: 'RAPID_GROWTH_ENVIRONMENT', impact: +1, desc: 'Scaling quickly' },
  { id: 'EXP20', name: 'PRIOR_CYBER_INCIDENT_HISTORY', impact: +2, desc: 'Previous cyber incidents' },
];

const INDUSTRY_CODES = [
  { code: '111110', title: 'Soybean Farming', score: 3, risks: ['Wire Transfer', 'System Intrusion', 'Business Interruption'] },
  { code: '221111', title: 'Hydroelectric Power Generation', score: 7, risks: ['Critical Infrastructure', 'OT Systems', 'Business Interruption'] },
  { code: '423710', title: 'Hardware Merchant Wholesalers', score: 6, risks: ['Wire Transfer', 'System Intrusion', 'Business Interruption'] },
  { code: '481111', title: 'Scheduled Passenger Air Transportation', score: 8, risks: ['Critical Infrastructure', 'PII', 'Business Interruption'] },
  { code: '511210', title: 'Software Publishers', score: 5, risks: ['IP Theft', 'System Intrusion', 'Data Breach'] },
  { code: '518210', title: 'Data Processing & Hosting', score: 7, risks: ['Data Breach', 'Business Interruption', 'Client Liability'] },
  { code: '522110', title: 'Commercial Banking', score: 8, risks: ['Wire Transfer', 'PII', 'Regulatory'] },
  { code: '524114', title: 'Direct Health Insurance Carriers', score: 7, risks: ['PHI', 'PII', 'Regulatory'] },
  { code: '541511', title: 'Custom Computer Programming', score: 5, risks: ['IP Theft', 'Client Liability', 'Data Breach'] },
  { code: '541512', title: 'Computer Systems Design', score: 6, risks: ['Admin Access', 'Client Liability', 'Data Breach'] },
  { code: '611110', title: 'Elementary and Secondary Schools', score: 5, risks: ['PII', 'Wire Transfer', 'Ransomware'] },
  { code: '621111', title: 'Offices of Physicians', score: 6, risks: ['PHI', 'PII', 'Regulatory'] },
  { code: '622110', title: 'General Medical Hospitals', score: 8, risks: ['PHI', 'Critical Infrastructure', 'OT Systems'] },
  { code: '721110', title: 'Hotels and Motels', score: 5, risks: ['PCI', 'PII', 'Business Interruption'] },
  { code: '722511', title: 'Full-Service Restaurants', score: 3, risks: ['PCI', 'Wire Transfer', 'Business Interruption'] },
];

const EXTERNAL_SIGNALS = [
  { id: 'EXPOSED_RDP', label: 'Exposed RDP', desc: 'Remote Desktop Protocol exposed to internet', icon: '🔓' },
  { id: 'OPEN_S3_BUCKET', label: 'Open S3 Bucket', desc: 'Publicly accessible cloud storage detected', icon: '🪣' },
  { id: 'KNOWN_DATA_BREACH', label: 'Known Data Breach', desc: 'Organization appeared in public breach database', icon: '💥' },
  { id: 'DARK_WEB_CREDENTIALS', label: 'Dark Web Credentials', desc: 'Employee credentials found on dark web marketplaces', icon: '🕵️' },
  { id: 'SSL_GRADE_LOW', label: 'SSL Grade Low', desc: 'Weak TLS/SSL configuration on public-facing services', icon: '🔑' },
  { id: 'DOMAIN_SPOOFING_RISK', label: 'Domain Spoofing Risk', desc: 'Domain lacks DMARC/DKIM protection against spoofing', icon: '📧' },
];

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'marketing', label: 'Marketing Materials' },
  { key: 'endorsements', label: 'Endorsements' },
  { key: 'applications', label: 'Applications' },
  { key: 'claims', label: 'Claims Resources' },
  { key: 'pricing', label: 'Pricing & Underwriting' },
  { key: 'risk', label: 'Risk Scoring' },
];

const RISK_SUBTABS = [
  { key: 'thresholds', label: 'Scale & Thresholds' },
  { key: 'industry', label: 'Industry Codes' },
  { key: 'tags', label: 'Control & Exposure Tags' },
  { key: 'signals', label: 'External Signals' },
];

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

const fmtCurrency = (val) => {
  if (val == null || val === '') return null;
  const num = Number(val);
  if (isNaN(num)) return String(val);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const getScoreColor = (score, max = 20) => {
  if (score <= 6) return 'green';
  if (score <= 13) return 'orange';
  return 'red';
};

const getHazardTier = (score) => {
  if (score <= 6) return 'Low';
  if (score <= 13) return 'Moderate';
  return 'High';
};

const renderObjectNice = (obj) => {
  if (obj == null) return null;
  if (typeof obj === 'string') return obj;
  if (typeof obj === 'object') {
    return Object.entries(obj).map(([k, v]) => (
      <div key={k} style={{ marginBottom: 4 }}>
        <strong>{k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ')}:</strong>{' '}
        {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
      </div>
    ));
  }
  return String(obj);
};

/* ─── Component ────────────────────────────────────────────────────────────── */

export function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('overview');
  const [riskSubTab, setRiskSubTab] = useState('thresholds');
  const [scaleMax, setScaleMax] = useState(20);

  // Industry codes filters
  const [industrySearch, setIndustrySearch] = useState('');
  const [hazardFilter, setHazardFilter] = useState('all');

  // Inline editing state
  const [editingScore, setEditingScore] = useState(null);
  const [editScoreValue, setEditScoreValue] = useState('');
  const [editingMultiplier, setEditingMultiplier] = useState(null);
  const [editMultiplierValue, setEditMultiplierValue] = useState('');
  const [industryOverrides, setIndustryOverrides] = useState({});
  const [multiplierOverrides, setMultiplierOverrides] = useState({});
  const [bulkAdjust, setBulkAdjust] = useState(0);
  const [selectedIndustries, setSelectedIndustries] = useState(new Set());
  const [editingTagImpact, setEditingTagImpact] = useState(null);
  const [editTagImpactValue, setEditTagImpactValue] = useState('');
  const [tagOverrides, setTagOverrides] = useState({});

  /* ── Load product ─────────────────────────────────── */

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await productsApi.get(id);
      const data = result.data || result;
      setProduct(data);
    } catch (err) {
      setError(err.message || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  /* ── Ownership check ──────────────────────────────── */

  const isOwner = useMemo(() => {
    if (!product || !user) return false;
    const owners = product.AssignedOwners || product.assignedOwners || [];
    return Array.isArray(owners) && owners.includes(user.userId);
  }, [product, user]);

  /* ── Filtered industry codes ──────────────────────── */

  const filteredIndustries = useMemo(() => {
    let list = INDUSTRY_CODES;
    if (industrySearch.trim()) {
      const q = industrySearch.toLowerCase();
      list = list.filter(
        (i) =>
          i.code.includes(q) ||
          i.title.toLowerCase().includes(q) ||
          i.risks.some((r) => r.toLowerCase().includes(q))
      );
    }
    if (hazardFilter !== 'all') {
      list = list.filter((i) => getHazardTier(i.score) === hazardFilter);
    }
    return list;
  }, [industrySearch, hazardFilter]);

  /* ── Threshold visualization values ───────────────── */

  const thresholds = useMemo(() => {
    if (scaleMax === 10) {
      return { green: 6, orange: 3, red: 1 };
    }
    return { green: 6, orange: 7, red: 7 };
  }, [scaleMax]);

  /* ── Loading state ────────────────────────────────── */

  if (loading) {
    return (
      <div className="pd__loading">
        <div className="spinner" />
        <span>Loading product...</span>
      </div>
    );
  }

  /* ── Error state ──────────────────────────────────── */

  if (error) {
    return (
      <div className="pd__error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={() => navigate('/products')}>
          Back to Products
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pd__error">
        <h2>Product Not Found</h2>
        <p>The requested product could not be found.</p>
        <button className="btn btn--primary" onClick={() => navigate('/products')}>
          Back to Products
        </button>
      </div>
    );
  }

  /* ── Derived product fields ───────────────────────── */

  const guardrails = product.PricingGuardrails || {};
  const isActive = product.Active !== false;

  /* ── Tab renderers ────────────────────────────────── */

  const renderOverview = () => {
    const appetite = product.AppetiteDefinition;
    return (
      <div className="pd__overview">
        {/* Description */}
        <div className="pd__overview-section card">
          <h4 className="pd__section-title">Product Description</h4>
          <p className="pd__description">
            {product.Description || 'No description provided.'}
          </p>
        </div>

        {/* Appetite Definition */}
        <div className="pd__overview-section card">
          <h4 className="pd__section-title">Appetite Definition</h4>
          {appetite != null && typeof appetite === 'object' ? (
            <div className="pd__appetite-cards">
              {Object.entries(appetite).map(([key, val]) => (
                <div key={key} className="pd__appetite-card card">
                  <div className="pd__appetite-card-label">
                    {key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ')}
                  </div>
                  <div className="pd__appetite-card-value">
                    {Array.isArray(val) ? val.join(', ') : String(val ?? '—')}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="pd__description">{String(appetite || 'Not yet defined.')}</p>
          )}
        </div>

        {/* Status & Meta */}
        <div className="pd__overview-section card">
          <h4 className="pd__section-title">Product Details</h4>
          <div className="pd__meta-grid">
            <div className="pd__meta-item">
              <div className="pd__meta-label">Status</div>
              <div className="pd__meta-value">
                <span className={`pd__status-badge pd__status-badge--${isActive ? 'active' : 'inactive'}`}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            {product.ProductID && (
              <div className="pd__meta-item">
                <div className="pd__meta-label">Product ID</div>
                <div className="pd__meta-value">{product.ProductID}</div>
              </div>
            )}
            {product.CreatedAt && (
              <div className="pd__meta-item">
                <div className="pd__meta-label">Created</div>
                <div className="pd__meta-value">
                  {new Date(product.CreatedAt).toLocaleDateString()}
                </div>
              </div>
            )}
            {product.UpdatedAt && (
              <div className="pd__meta-item">
                <div className="pd__meta-label">Last Updated</div>
                <div className="pd__meta-value">
                  {new Date(product.UpdatedAt).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPlaceholder = (icon, title, text, showUpload = false) => (
    <div className="pd__placeholder card">
      <div className="pd__placeholder-icon">{icon}</div>
      <h3 className="pd__placeholder-title">{title}</h3>
      <p className="pd__placeholder-text">{text}</p>
      {showUpload && (
        <div className="pd__upload-area">
          <div className="pd__upload-icon">📤</div>
          <span>Drag files here or click to upload</span>
          <button className="btn btn--secondary">Choose Files</button>
        </div>
      )}
    </div>
  );

  const renderPricing = () => {
    const minPremium = guardrails.MinPremium ?? guardrails.min_premium;
    const maxPremium = guardrails.MaxPremium ?? guardrails.max_premium;
    const minDeductible = guardrails.MinDeductible ?? guardrails.min_deductible;
    const maxDeductible = guardrails.MaxDeductible ?? guardrails.max_deductible;
    const minLimit = guardrails.MinLimit ?? guardrails.min_limit;
    const maxLimit = guardrails.MaxLimit ?? guardrails.max_limit;

    const guidelines = product.UnderwritingGuidelines;
    const exceptions = product.ExceptionRules;

    return (
      <div className="pd__pricing">
        {/* Header with edit button */}
        <div className="pd__pricing-header">
          <h4 className="pd__section-title" style={{ flex: 1 }}>Pricing Guardrails</h4>
          {isOwner && (
            <button
              className="btn btn--secondary"
              onClick={() => navigate(`/products`)}
            >
              Edit Pricing
            </button>
          )}
        </div>

        {/* Guardrails grid */}
        <div className="pd__guardrails-grid">
          <div className="pd__guardrail-item">
            <div className="pd__guardrail-label">Min Premium</div>
            <div className={`pd__guardrail-value ${minPremium == null ? 'pd__guardrail-value--empty' : ''}`}>
              {fmtCurrency(minPremium) || 'Not set'}
            </div>
          </div>
          <div className="pd__guardrail-item">
            <div className="pd__guardrail-label">Max Premium</div>
            <div className={`pd__guardrail-value ${maxPremium == null ? 'pd__guardrail-value--empty' : ''}`}>
              {fmtCurrency(maxPremium) || 'Not set'}
            </div>
          </div>
          <div className="pd__guardrail-item">
            <div className="pd__guardrail-label">Min Deductible</div>
            <div className={`pd__guardrail-value ${minDeductible == null ? 'pd__guardrail-value--empty' : ''}`}>
              {fmtCurrency(minDeductible) || 'Not set'}
            </div>
          </div>
          <div className="pd__guardrail-item">
            <div className="pd__guardrail-label">Max Deductible</div>
            <div className={`pd__guardrail-value ${maxDeductible == null ? 'pd__guardrail-value--empty' : ''}`}>
              {fmtCurrency(maxDeductible) || 'Not set'}
            </div>
          </div>
          <div className="pd__guardrail-item">
            <div className="pd__guardrail-label">Min Limit</div>
            <div className={`pd__guardrail-value ${minLimit == null ? 'pd__guardrail-value--empty' : ''}`}>
              {fmtCurrency(minLimit) || 'Not set'}
            </div>
          </div>
          <div className="pd__guardrail-item">
            <div className="pd__guardrail-label">Max Limit</div>
            <div className={`pd__guardrail-value ${maxLimit == null ? 'pd__guardrail-value--empty' : ''}`}>
              {fmtCurrency(maxLimit) || 'Not set'}
            </div>
          </div>
        </div>

        {/* Underwriting Guidelines */}
        <div className="card">
          <div className="pd__pricing-header">
            <h4 className="pd__section-title" style={{ flex: 1 }}>Underwriting Guidelines</h4>
            {isOwner && (
              <button
                className="btn btn--secondary"
                onClick={() => navigate(`/products`)}
              >
                Edit Guidelines
              </button>
            )}
          </div>
          {guidelines ? (
            typeof guidelines === 'object' ? (
              <div className="pd__description">{renderObjectNice(guidelines)}</div>
            ) : (
              <p className="pd__guidelines-text">{guidelines}</p>
            )
          ) : (
            <p className="pd__empty-text">No underwriting guidelines defined.</p>
          )}
        </div>

        {/* Exception Rules */}
        <div className="card">
          <h4 className="pd__section-title">Exception Rules</h4>
          {exceptions ? (
            <div className="pd__exceptions-block">
              {typeof exceptions === 'object'
                ? JSON.stringify(exceptions, null, 2)
                : exceptions}
            </div>
          ) : (
            <p className="pd__empty-text">No exception rules defined.</p>
          )}
        </div>
      </div>
    );
  };

  /* ── Risk Scoring sub-tab renderers ───────────────── */

  const renderThresholds = () => (
    <div className="pd__scale-section">
      <div className="pd__scale-toggle">
        <span className="pd__scale-toggle-label">Risk Scale:</span>
        <div className="pd__scale-toggle-btns">
          <button
            className={`pd__scale-btn ${scaleMax === 10 ? 'pd__scale-btn--active' : ''}`}
            onClick={() => setScaleMax(10)}
          >
            1 – 10
          </button>
          <button
            className={`pd__scale-btn ${scaleMax === 20 ? 'pd__scale-btn--active' : ''}`}
            onClick={() => setScaleMax(20)}
          >
            1 – 20
          </button>
        </div>
      </div>

      <div className="card">
        <h4 className="pd__section-title">Threshold Visualization</h4>
        <div className="pd__threshold-bar-container">
          <div className="pd__threshold-bar-header">
            <span>1</span>
            <span>{scaleMax}</span>
          </div>
          <div className="pd__threshold-bar">
            <div
              className="pd__threshold-segment pd__threshold-segment--green"
              style={{ flex: thresholds.green }}
            >
              1 – {thresholds.green}
            </div>
            <div
              className="pd__threshold-segment pd__threshold-segment--orange"
              style={{ flex: thresholds.orange }}
            >
              {thresholds.green + 1} – {thresholds.green + thresholds.orange}
            </div>
            <div
              className="pd__threshold-segment pd__threshold-segment--red"
              style={{ flex: thresholds.red }}
            >
              {thresholds.green + thresholds.orange + 1} – {scaleMax}
            </div>
          </div>
          <div className="pd__threshold-labels">
            <div className="pd__threshold-label">
              <span className="pd__threshold-dot pd__threshold-dot--green" />
              Acceptable Risk
            </div>
            <div className="pd__threshold-label">
              <span className="pd__threshold-dot pd__threshold-dot--orange" />
              Elevated / Refer
            </div>
            <div className="pd__threshold-label">
              <span className="pd__threshold-dot pd__threshold-dot--red" />
              High Risk / Decline
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleScoreEdit = (code, currentScore) => {
    setEditingScore(code);
    setEditScoreValue(String(industryOverrides[code] ?? currentScore));
  };

  const handleScoreSave = (code) => {
    const val = parseInt(editScoreValue, 10);
    if (!isNaN(val) && val >= 1 && val <= scaleMax) {
      setIndustryOverrides((prev) => ({ ...prev, [code]: val }));
    }
    setEditingScore(null);
  };

  const handleMultiplierEdit = (code, current) => {
    setEditingMultiplier(code);
    setEditMultiplierValue(String(multiplierOverrides[code] ?? current));
  };

  const handleMultiplierSave = (code) => {
    const val = parseFloat(editMultiplierValue);
    if (!isNaN(val) && val >= 0.1 && val <= 5.0) {
      setMultiplierOverrides((prev) => ({ ...prev, [code]: val }));
    }
    setEditingMultiplier(null);
  };

  const handleBulkAdjust = () => {
    if (selectedIndustries.size === 0 || bulkAdjust === 0) return;
    const updates = {};
    selectedIndustries.forEach((code) => {
      const ind = INDUSTRY_CODES.find((i) => i.code === code);
      if (ind) {
        const current = industryOverrides[code] ?? ind.score;
        updates[code] = Math.max(1, Math.min(scaleMax, current + bulkAdjust));
      }
    });
    setIndustryOverrides((prev) => ({ ...prev, ...updates }));
    setSelectedIndustries(new Set());
    setBulkAdjust(0);
  };

  const toggleIndustrySelection = (code) => {
    setSelectedIndustries((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleAllIndustries = () => {
    if (selectedIndustries.size === filteredIndustries.length) {
      setSelectedIndustries(new Set());
    } else {
      setSelectedIndustries(new Set(filteredIndustries.map((i) => i.code)));
    }
  };

  const renderIndustryCodes = () => (
    <div className="pd__industry-section">
      <div className="pd__industry-controls">
        <input
          className="pd__search-input"
          type="text"
          placeholder="Search by NAICS code, industry name, or risk..."
          value={industrySearch}
          onChange={(e) => setIndustrySearch(e.target.value)}
        />
        <select
          className="pd__filter-select"
          value={hazardFilter}
          onChange={(e) => setHazardFilter(e.target.value)}
        >
          <option value="all">All Tiers</option>
          <option value="Low">Low Hazard (1-6)</option>
          <option value="Moderate">Moderate Hazard (7-13)</option>
          <option value="High">High Hazard (14-20)</option>
        </select>
      </div>

      {/* Bulk appetite adjustment */}
      {selectedIndustries.size > 0 && (
        <div className="pd__bulk-adjust card">
          <span className="pd__bulk-label">{selectedIndustries.size} selected</span>
          <div className="pd__bulk-controls">
            <button className="pd__bulk-btn" onClick={() => setBulkAdjust((p) => p - 1)}>-</button>
            <span className="pd__bulk-value">{bulkAdjust > 0 ? '+' : ''}{bulkAdjust}</span>
            <button className="pd__bulk-btn" onClick={() => setBulkAdjust((p) => p + 1)}>+</button>
          </div>
          <button className="btn btn--primary btn--sm" onClick={handleBulkAdjust} disabled={bulkAdjust === 0}>
            Apply Adjustment
          </button>
          <button className="btn btn--secondary btn--sm" onClick={() => { setSelectedIndustries(new Set()); setBulkAdjust(0); }}>
            Clear
          </button>
        </div>
      )}

      <div className="pd__table-wrap">
        <table className="pd__table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" checked={selectedIndustries.size === filteredIndustries.length && filteredIndustries.length > 0} onChange={toggleAllIndustries} />
              </th>
              <th>NAICS Code</th>
              <th>Industry Title</th>
              <th>Base Score</th>
              <th>Freq/Sev Multiplier</th>
              <th>Hazard Tier</th>
              <th>Top Risks</th>
            </tr>
          </thead>
          <tbody>
            {filteredIndustries.length > 0 ? (
              filteredIndustries.map((ind) => {
                const effectiveScore = industryOverrides[ind.code] ?? ind.score;
                const multiplier = multiplierOverrides[ind.code] ?? 1.0;
                const color = getScoreColor(effectiveScore, scaleMax);
                const tier = getHazardTier(effectiveScore);
                const isModified = industryOverrides[ind.code] !== undefined;
                const isMultModified = multiplierOverrides[ind.code] !== undefined;
                return (
                  <tr key={ind.code} className={selectedIndustries.has(ind.code) ? 'pd__row--selected' : ''}>
                    <td>
                      <input type="checkbox" checked={selectedIndustries.has(ind.code)} onChange={() => toggleIndustrySelection(ind.code)} />
                    </td>
                    <td className="pd__table-code">{ind.code}</td>
                    <td className="pd__table-industry">{ind.title}</td>
                    <td>
                      {editingScore === ind.code ? (
                        <div className="pd__inline-edit">
                          <input
                            type="number"
                            min={1}
                            max={scaleMax}
                            value={editScoreValue}
                            onChange={(e) => setEditScoreValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleScoreSave(ind.code); if (e.key === 'Escape') setEditingScore(null); }}
                            autoFocus
                            className="pd__inline-input"
                          />
                          <button className="pd__inline-save" onClick={() => handleScoreSave(ind.code)}>Save</button>
                        </div>
                      ) : (
                        <span
                          className={`pd__score-badge pd__score-badge--${color} ${isModified ? 'pd__score-badge--modified' : ''}`}
                          onClick={() => handleScoreEdit(ind.code, effectiveScore)}
                          title="Click to edit"
                          style={{ cursor: 'pointer' }}
                        >
                          {effectiveScore}{isModified && ' *'}
                        </span>
                      )}
                    </td>
                    <td>
                      {editingMultiplier === ind.code ? (
                        <div className="pd__inline-edit">
                          <input
                            type="number"
                            min={0.1}
                            max={5.0}
                            step={0.1}
                            value={editMultiplierValue}
                            onChange={(e) => setEditMultiplierValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleMultiplierSave(ind.code); if (e.key === 'Escape') setEditingMultiplier(null); }}
                            autoFocus
                            className="pd__inline-input"
                          />
                          <button className="pd__inline-save" onClick={() => handleMultiplierSave(ind.code)}>Save</button>
                        </div>
                      ) : (
                        <span
                          className={`pd__multiplier ${isMultModified ? 'pd__multiplier--modified' : ''}`}
                          onClick={() => handleMultiplierEdit(ind.code, multiplier)}
                          title="Click to edit frequency/severity multiplier"
                          style={{ cursor: 'pointer' }}
                        >
                          {multiplier.toFixed(1)}x{isMultModified && ' *'}
                        </span>
                      )}
                    </td>
                    <td>{tier}</td>
                    <td>
                      <div className="pd__risk-tags">
                        {ind.risks.map((r) => (
                          <span key={r} className="pd__risk-tag">{r}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7} className="pd__table-empty">
                  No industries match your search criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const handleTagImpactEdit = (tagId, currentImpact) => {
    setEditingTagImpact(tagId);
    setEditTagImpactValue(String(tagOverrides[tagId] ?? currentImpact));
  };

  const handleTagImpactSave = (tagId) => {
    const val = parseInt(editTagImpactValue, 10);
    if (!isNaN(val) && val >= -20 && val <= 20) {
      setTagOverrides((prev) => ({ ...prev, [tagId]: val }));
    }
    setEditingTagImpact(null);
  };

  const renderTagPanel = (title, tags) => (
    <div className="pd__tags-panel">
      <div className="pd__tags-panel-header">
        <h5 className="pd__tags-panel-title">{title}</h5>
        <span className="pd__tags-panel-count">{tags.length} tags</span>
      </div>
      <ul className="pd__tag-list">
        {tags.map((tag) => {
          const effectiveImpact = tagOverrides[tag.id] ?? tag.impact;
          const isMitigating = effectiveImpact < 0;
          const isModified = tagOverrides[tag.id] !== undefined;
          return (
            <li key={tag.id} className="pd__tag-item">
              <span className="pd__tag-id">{tag.id}</span>
              <div className="pd__tag-info">
                <div className="pd__tag-name">{tag.name}</div>
                <div className="pd__tag-desc">{tag.desc}</div>
              </div>
              {editingTagImpact === tag.id ? (
                <div className="pd__inline-edit">
                  <input
                    type="number"
                    min={-20}
                    max={20}
                    value={editTagImpactValue}
                    onChange={(e) => setEditTagImpactValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleTagImpactSave(tag.id); if (e.key === 'Escape') setEditingTagImpact(null); }}
                    autoFocus
                    className="pd__inline-input pd__inline-input--sm"
                  />
                  <button className="pd__inline-save" onClick={() => handleTagImpactSave(tag.id)}>Save</button>
                </div>
              ) : (
                <span
                  className={`pd__tag-impact ${
                    isMitigating ? 'pd__tag-impact--mitigating' : 'pd__tag-impact--risk'
                  } ${isModified ? 'pd__tag-impact--modified' : ''}`}
                  onClick={() => handleTagImpactEdit(tag.id, effectiveImpact)}
                  title="Click to edit impact"
                  style={{ cursor: 'pointer' }}
                >
                  {effectiveImpact > 0 ? '+' : ''}{effectiveImpact}{isModified ? ' *' : ''}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  const renderTags = () => (
    <div className="pd__tags-section">
      <h4 className="pd__section-title">Risk Adjustment Tags (1-20 Scale)</h4>
      <div className="pd__tags-panels">
        {renderTagPanel('Control Tags', CONTROL_TAGS)}
        {renderTagPanel('Exposure Tags', EXPOSURE_TAGS)}
      </div>
    </div>
  );

  const renderSignals = () => (
    <div className="pd__signals-section">
      <h4 className="pd__section-title">External Signal Tags</h4>
      <p className="pd__description" style={{ marginBottom: 'var(--space-md)' }}>
        Signals sourced from external scanning and threat intelligence feeds. These are applied
        automatically during underwriting when external data matches known risk indicators.
      </p>
      <div className="pd__signals-grid">
        {EXTERNAL_SIGNALS.map((sig) => (
          <div key={sig.id} className="pd__signal-card card">
            <div className="pd__signal-icon">{sig.icon}</div>
            <div>
              <div className="pd__signal-name">{sig.label}</div>
              <div className="pd__signal-desc">{sig.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRiskScoring = () => (
    <div className="pd__risk-scoring">
      <div className="pd__subtabs">
        {RISK_SUBTABS.map((st) => (
          <button
            key={st.key}
            className={`pd__subtab ${riskSubTab === st.key ? 'pd__subtab--active' : ''}`}
            onClick={() => setRiskSubTab(st.key)}
          >
            {st.label}
          </button>
        ))}
      </div>

      {riskSubTab === 'thresholds' && renderThresholds()}
      {riskSubTab === 'industry' && renderIndustryCodes()}
      {riskSubTab === 'tags' && renderTags()}
      {riskSubTab === 'signals' && renderSignals()}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'marketing':
        return renderPlaceholder(
          '📄',
          'Marketing Materials',
          'Upload brochures, one-pagers, and marketing collateral for this product line.',
          true
        );
      case 'endorsements':
        return renderPlaceholder(
          '📑',
          'Policy Endorsements',
          'No endorsements have been added yet. Endorsements modify standard policy terms and conditions.'
        );
      case 'applications':
        return renderPlaceholder(
          '📋',
          'Product Applications',
          'No applications configured yet. Application forms and supplemental questionnaires will appear here.'
        );
      case 'claims':
        return renderPlaceholder(
          '🛡️',
          'Claims Resources',
          'No claims resources available. Upload claims guides, procedures, and reporting templates.'
        );
      case 'pricing':
        return renderPricing();
      case 'risk':
        return renderRiskScoring();
      default:
        return null;
    }
  };

  /* ── Render ───────────────────────────────────────── */

  return (
    <div className="pd">
      <Link to="/products" className="pd__back">
        ← Back to Products
      </Link>

      <div className="pd__header">
        <h1 className="pd__title">{product.Name || 'Untitled Product'}</h1>
        <span
          className={`pd__status-badge pd__status-badge--${isActive ? 'active' : 'inactive'}`}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="pd__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`pd__tab ${activeTab === tab.key ? 'pd__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="pd__content">{renderTabContent()}</div>
    </div>
  );
}
